import { useCallback, useState, Suspense } from 'react'
import { css } from '@emotion/react'
import styled from '@emotion/styled'
import Paper from '@mui/material/Paper'
import Snackbar from '@mui/material/Snackbar'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import {
  DatasetSubmissionOutboundIntegration,
  ITargetRows,
  SubmissionWithModDatasetPosts,
} from '@datasets/dataset-details/datasetHistoryV2/types'
import { resyncAllFailedIntegrationAssignments } from '@datasets/dataset-details/datasetHistoryV2/utils'
import { lazyImport } from '@global/utils/lazyImport'
import { OutboundDestinationsTableRow } from './components'
import { TargetTableRow } from './components/targetTableRow'
import { t } from '@lingui/core/macro'
import { DatasetStatus } from '@components'

const { Drawer } = lazyImport(
  () => import(/* webpackChunkName: "outboundDestinationsDrawer" */ './components/drawer'),
)

const StyledTableCell = styled(TableCell)`
  border-color: #e2e2e2 !important;
  background-color: #f9f9f9;
  color: #8a8a8a;
`

const cssTableContainer = css`
  .MuiTableCell-root:first-of-type {
    padding-left: 60px;
  }

  .MuiTableCell-root:last-child {
    padding-right: 60px;
  }
`

interface IProps {
  outboundIntegrations: DatasetSubmissionOutboundIntegration[]
  datasetSubmissionId: number
  getSelectedSubmissionData: () => SubmissionWithModDatasetPosts | undefined
}

export const OutboundDestinationsTable = ({
  outboundIntegrations,
  datasetSubmissionId,
  getSelectedSubmissionData,
}: IProps) => {
  const [drawerIsOpen, setDrawerIsOpen] = useState(false)
  const [selectedOutbound, setSelectedOutbound] =
    useState<DatasetSubmissionOutboundIntegration | null>(null)
  const [selectedTarget, setSelectedTarget] = useState<string | null>(null)
  const syncButtonIsDisabled = false
  const [snackbarIsOpen, setSnackbarIsOpen] = useState(false)

  const submission = getSelectedSubmissionData()

  const handleOpenDrawerOutbound = (outbound: DatasetSubmissionOutboundIntegration) => {
    setSelectedOutbound(outbound)
    setSelectedTarget(null)
    setDrawerIsOpen(true)
  }

  const handleOpenDrawerTarget = useCallback(target => {
    setSelectedTarget(target)
    setSelectedOutbound(null)
    setDrawerIsOpen(true)
  }, [])

  const handleSyncOutbound = (outbound: DatasetSubmissionOutboundIntegration) => {
    setDrawerIsOpen(false)
    setSnackbarIsOpen(true)
    void resyncAllFailedIntegrationAssignments(outbound.integrationAssignments, datasetSubmissionId)
  }

  // Dataset Posts may have targets, which need to be grouped by slug; to show as table rows,
  // and then shown in a list within the drawer.
  const targetRows: ITargetRows =
    submission?.dataset_posts?.reduce((acc, post) => {
      const slug = post?.target?.connector?.connector_type?.slug
      const obj = { ...acc }

      if (!acc[slug]) {
        obj[slug] = {
          status: post?.status,
          slug,
          name: post?.target?.connector?.connector_type?.name,
          datasetPosts: [post],
        }

        return obj
      }

      obj[slug] = {
        ...obj[slug],
        datasetPosts: [...(obj[slug].datasetPosts || []), post],
      }

      return obj
    }, {}) ?? {}

  const selectedTargetRow = selectedTarget ? targetRows[selectedTarget] : null

  return (
    <TableContainer component={Paper} elevation={0} css={cssTableContainer}>
      <Snackbar
        open={snackbarIsOpen}
        autoHideDuration={6000}
        onClose={() => {
          setSnackbarIsOpen(false)
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message={t`Re-syncing failed batches, please come back later.`}
      />
      <Table sx={{ minWidth: 650 }} aria-label="integrations table">
        <TableHead>
          <TableRow>
            <StyledTableCell>{t`Outbound integration`}</StyledTableCell>
            <StyledTableCell align="center">{t`Destinations`}</StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {outboundIntegrations.map(outbound => (
            <OutboundDestinationsTableRow
              outbound={outbound}
              key={outbound.id}
              submissionStatus={submission?.status as DatasetStatus}
              datasetSubmissionId={datasetSubmissionId}
              onClickOpenDrawer={() => {
                handleOpenDrawerOutbound(outbound)
              }}
            />
          ))}
          {Object.values(targetRows).map(row => (
            <TargetTableRow
              key={row.slug}
              onClickOpenDrawer={handleOpenDrawerTarget}
              submissionStatus={submission?.status as DatasetStatus}
              {...row}
            />
          ))}
        </TableBody>
      </Table>
      {selectedOutbound || selectedTarget ? (
        <Suspense fallback={null}>
          <Drawer
            open={drawerIsOpen}
            onClose={() => {
              setSelectedTarget(null)
              setDrawerIsOpen(false)
            }}
            selectedTargetRow={selectedTargetRow ?? null}
            selectedOutbound={selectedOutbound}
            syncButtonIsDisabled={syncButtonIsDisabled}
            handleSyncOutbound={handleSyncOutbound}
            datasetSubmissionId={datasetSubmissionId}
            getSelectedSubmissionData={getSelectedSubmissionData}
          />
        </Suspense>
      ) : null}
    </TableContainer>
  )
}
